/**
 * «Cambió el plano» (DWG/DXF): saca la FIRMA de cada entidad de un DXF (la que compara `diffEntidades` de shared).
 *
 * Se lee con el mismo lector que el visor (`@mlightcad/data-model`), sin vista: no dibuja nada. Cada entidad se reduce a
 * tipo + puntos en METROS (coordenadas × `factor`) + atributos (capa, texto, bloque). Solo la modelspace y solo el primer
 * nivel: el contenido de un bloque no se expande (un cambio DENTRO de un bloque no se ve; sí si se agrega o quita la
 * inserción). Cualquier entidad cuya geometría no se pueda leer cae a su caja (extensión); si ni eso, se omite.
 *
 * Los handles (objectId) son los del lector del visor: sirven para pintar en el visor del MISMO archivo.
 */
import type { FirmaEntidad } from '@supernova/shared';

type Pt = readonly [number, number];
type AnyEnt = Record<string, unknown> & { objectId?: string; layer?: string; geometricExtents?: { min: { x: number; y: number }; max: { x: number; y: number } } };

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const pt = (p: unknown, f: number): Pt | null => {
  const o = p as { x?: unknown; y?: unknown } | null | undefined;
  const x = num(o?.x);
  const y = num(o?.y);
  return x == null || y == null ? null : [x * f, y * f];
};
const cmp = (a: Pt, b: Pt) => a[0] - b[0] || a[1] - b[1];
const lineaOrdenada = (a: Pt, b: Pt): Pt[] => (cmp(a, b) <= 0 ? [a, b] : [b, a]);
const redondea = (n: number) => Math.round(n * 1000) / 1000;

/** Una polilínea: abierta, el extremo menor primero; cerrada, empieza por el vértice menor y sigue por el sentido menor. */
function normalizaPolilinea(pts: Pt[], cerrada: boolean): Pt[] {
  if (pts.length < 2) return pts;
  if (!cerrada) return cmp(pts[0]!, pts[pts.length - 1]!) <= 0 ? pts : [...pts].reverse();
  let ini = 0;
  for (let i = 1; i < pts.length; i++) if (cmp(pts[i]!, pts[ini]!) < 0) ini = i;
  const adelante = pts.map((_, k) => pts[(ini + k) % pts.length]!);
  const atras = pts.map((_, k) => pts[(ini - k + pts.length) % pts.length]!);
  for (let k = 1; k < pts.length; k++) {
    const c = cmp(adelante[k]!, atras[k]!);
    if (c !== 0) return c < 0 ? adelante : atras;
  }
  return adelante;
}

const alFinalDelHilo = () => new Promise<void>((ok) => setTimeout(ok, 0));

/** Lee el DXF y devuelve la firma de cada entidad de la modelspace. `factor` = metros por unidad del dibujo. */
export async function firmasDeDxf(buf: ArrayBuffer, factor: number, o: { signal?: AbortSignal } = {}): Promise<FirmaEntidad[]> {
  const f = Number.isFinite(factor) && factor > 0 ? factor : 1;
  const dm = (await import('@mlightcad/data-model')) as unknown as Record<string, new (...a: unknown[]) => unknown> & { AcDbDatabase: new () => { read(d: ArrayBuffer, o: unknown, t?: string): Promise<void>; tables: { blockTable: { modelSpace: { newIterator(): Iterable<unknown> } } } } };
  const db = new dm.AcDbDatabase();
  // Se le da una COPIA: el lector puede dejar el buffer inservible y el visor necesita el suyo.
  await db.read(buf.slice(0), { readOnly: true }, 'dxf');
  const es = (e: unknown, nombre: string) => {
    const C = dm[nombre] as unknown as (abstract new (...a: never[]) => unknown) | undefined;
    return !!C && e instanceof C;
  };

  const out: FirmaEntidad[] = [];
  let n = 0;
  for (const ent of db.tables.blockTable.modelSpace.newIterator()) {
    if (o.signal?.aborted) throw new DOMException('cancelado', 'AbortError');
    if (++n % 4000 === 0) await alFinalDelHilo();
    const e = ent as AnyEnt;
    const handle = e.objectId;
    if (!handle) continue;
    const capa = String(e.layer ?? '0');
    let tipo = String((e as { type?: unknown }).type ?? 'ENT');
    let puntos: Pt[] | null = null;
    let extra = '';
    let texto = '';
    let bloque = '';
    try {
      if (es(e, 'AcDbLine')) {
        const a = pt(e.startPoint, f);
        const b = pt(e.endPoint, f);
        if (a && b) puntos = lineaOrdenada(a, b);
      } else if (es(e, 'AcDbPolyline')) {
        const k = num(e.numberOfVertices) ?? 0;
        const v: Pt[] = [];
        for (let i = 0; i < k; i++) {
          const p = pt((e as { getPoint2dAt(i: number): unknown }).getPoint2dAt(i), f);
          if (p) v.push(p);
        }
        if (v.length === k && k > 0) puntos = normalizaPolilinea(v, e.closed === true);
      } else if (es(e, 'AcDb2dPolyline') || es(e, 'AcDb3dPolyline')) {
        const k = num(e.numberOfVertices) ?? 0;
        const v: Pt[] = [];
        for (let i = 0; i < k; i++) {
          const p = pt((e as { getPointAt(i: number): unknown }).getPointAt(i), f);
          if (p) v.push(p);
        }
        if (v.length === k && k > 0) puntos = normalizaPolilinea(v, e.closed === true);
      } else if (es(e, 'AcDbArc')) {
        const c = pt(e.center, f);
        const a = pt(e.startPoint, f);
        const b = pt(e.endPoint, f);
        if (c && a && b) puntos = [c, a, b];
      } else if (es(e, 'AcDbCircle')) {
        const c = pt(e.center, f);
        const r = num(e.radius);
        if (c && r != null) puntos = [c, [c[0] + r * f, c[1]]];
      } else if (es(e, 'AcDbEllipse')) {
        const c = pt(e.center, f);
        if (c) {
          puntos = [c];
          extra = `${redondea((num(e.majorAxisRadius) ?? 0) * f)}/${redondea((num(e.minorAxisRadius) ?? 0) * f)}/${redondea(num(e.startAngle) ?? 0)}/${redondea(num(e.endAngle) ?? 0)}`;
        }
      } else if (es(e, 'AcDbMText')) {
        const p = pt(e.location, f);
        if (p) {
          puntos = [p];
          texto = String(e.contents ?? '').replace(/\\[A-Za-z][^;]*;|[{}]/g, '').trim();
          extra = String(redondea((num(e.height) ?? 0) * f));
        }
      } else if (es(e, 'AcDbText')) {
        const p = pt(e.position, f);
        if (p) {
          puntos = [p];
          texto = String(e.textString ?? '').trim();
          extra = String(redondea((num(e.height) ?? 0) * f));
        }
      } else if (es(e, 'AcDbBlockReference')) {
        const p = pt(e.position, f);
        if (p) {
          puntos = [p];
          bloque = String(e.blockName ?? '');
          // Los bloques anónimos (*U, *D…) cambian de nombre al guardar: no cuentan como atributo.
          if (bloque.startsWith('*')) bloque = '';
          const s = e.scaleFactors as { x?: unknown; y?: unknown } | undefined;
          extra = `${redondea(num(e.rotation) ?? 0)}/${redondea(num(s?.x) ?? 1)}/${redondea(num(s?.y) ?? 1)}`;
          tipo = 'INSERT';
        }
      } else if (es(e, 'AcDbPoint')) {
        const p = pt(e.position, f);
        if (p) puntos = [p];
      } else if (es(e, 'AcDbDimension')) {
        const p = pt(e.textPosition, f);
        if (p) {
          puntos = [p];
          texto = String(e.dimensionText ?? e.measurement ?? '').trim();
        }
      }
    } catch {
      puntos = null;
    }
    if (!puntos) {
      // Sin lector específico (hatch, spline, sólidos…): su caja, que es lo que mueve la vista.
      try {
        const x = e.geometricExtents;
        const a = pt(x?.min, f);
        const b = pt(x?.max, f);
        if (a && b) puntos = [a, b];
      } catch {
        /* sin extensión: se omite */
      }
    }
    if (!puntos || puntos.length === 0) continue;
    out.push({ handle, tipo, puntos, atributos: `${capa}|${texto}|${bloque}|${extra}` });
  }
  return out;
}
